/**
 * AddSectionModal — Iter 144 visual redesign.
 *
 * Spec (user request):
 *   • Centered modal overlay (max 1400×85vh), NOT a full-page replacement
 *     — the user stays connected to the website behind it (dimmed).
 *   • Visual previews: real stock photos + headlines + buttons + cards
 *     rendered as miniature screenshots via `<RichTilePreview/>`.
 *   • Larger cards (~340px wide, 16:10 aspect ratio).
 *   • Sidebar: emoji icons, active highlight, sticky scroll.
 *   • Hover: scale + shadow + outline + "Insert" pill.
 *   • Search at top + badges (Popular / New / Featured).
 *   • Instant insert with auto-scroll + flash (wired in DashboardContent).
 *
 * Industry filters + Live Preview drawer + AI dynamic swap are deferred
 * to higher-tier websites (P1 backlog).
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Search, Sparkles, Bookmark, Trash2, Eye, SlidersHorizontal } from 'lucide-react';
import { SECTION_CATEGORIES, SECTION_TEMPLATES } from './sectionLayouts';
import { CATEGORY_ICONS, GROUP_ORDER, TEMPLATE_BADGES } from './sections/categoryMeta';
import RichTilePreview from './sections/RichTilePreview';
import LiveSectionPreview, { hasLiveRenderer } from './sections/LiveSectionPreview';
import { FILTER_DIMENSIONS, matchesFilters } from './sections/tagRules';
import { fuzzyFilter } from './sections/fuzzySearch';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BADGE_STYLES = {
  POPULAR:  { bg: '#FEF3C7', fg: '#92400E', label: 'Popular' },
  NEW:      { bg: '#DCFCE7', fg: '#166534', label: 'New' },
  FEATURED: { bg: '#EDE9FE', fg: '#5B21B6', label: 'Featured' },
};

export default function AddSectionModal({ open, onClose, onAdd, defaultCategory = 'introduce' }) {
  const [activeCat, setActiveCat] = useState(defaultCategory);
  const [query, setQuery] = useState('');
  const [savedLayouts, setSavedLayouts] = useState([]);
  // Iter 189d Phase C — filter chip state. Each dimension holds a Set of
  // selected option ids. Empty set = no filter for that dimension.
  const [filters, setFilters] = useState({
    density:  new Set(),
    style:    new Set(),
    industry: new Set(),
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const toggleFilter = (dim, id) => {
    setFilters(prev => {
      const next = new Set(prev[dim]);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...prev, [dim]: next };
    });
  };
  const clearFilters = () => setFilters({ density: new Set(), style: new Set(), industry: new Set() });
  const activeFilterCount = filters.density.size + filters.style.size + filters.industry.size;
  const searchRef = useRef(null);
  // Iter 189c Phase A — hover-triggered LIVE preview. Uses the actual
  // section renderer (HeroBlock, AboutBlock, …) so preview = insertion.
  // Iter 189f — swap moved INSIDE the tile itself so the preview no
  // longer covers other tiles. Shorter delay because the swap is in-place.
  const [previewTpl, setPreviewTpl] = useState(null);
  const previewTimerRef = useRef(null);
  const scheduleHoverPreview = (tpl) => {
    clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => setPreviewTpl(tpl), 120);
  };
  const cancelHoverPreview = () => {
    clearTimeout(previewTimerRef.current);
    previewTimerRef.current = null;
  };

  // Fetch saved layouts when modal opens (Iter 149 "My Layouts" tab).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios.get(`${API}/builder/saved_layouts`).then(r => {
      if (!cancelled && Array.isArray(r.data)) setSavedLayouts(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  const deleteSaved = async (id) => {
    if (!(await themedConfirm('Delete this saved layout?'))) return;
    try {
      await axios.delete(`${API}/builder/saved_layouts/${id}`);
      setSavedLayouts(s => s.filter(x => x.id !== id));
    } catch (e) { /* deletion failure tolerated */ }
  };

  // Focus the search input + lock body scroll when modal opens.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setTimeout(() => searchRef.current?.focus(), 100);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // ── Group categories by their `group` field, ordering by GROUP_ORDER.
  const groupedCats = useMemo(() => {
    const groups = {};
    SECTION_CATEGORIES.forEach(c => {
      const key = c.group || 'Top';
      if (!groups[key]) groups[key] = [];
      groups[key].push(c);
    });
    return groups;
  }, []);

  // ── Templates list. When the user is searching, search ACROSS every
  //    category (not just the active one) so vendors find what they
  //    want regardless of where the layout lives. Filter chips apply
  //    on top of both search and category selection (Iter 189d Phase C).
  const visibleTemplates = useMemo(() => {
    const q = query.trim();
    let base;
    if (q) {
      const all = [];
      Object.entries(SECTION_TEMPLATES).forEach(([catId, tpls]) => {
        tpls.forEach(tpl => all.push({ ...tpl, _categoryId: catId }));
      });
      // Fuzzy — search across label + id + category label so partial
      // typos, subsequences, and category names all match.
      base = fuzzyFilter(
        all,
        (tpl) => [
          tpl.label,
          tpl.id,
          SECTION_CATEGORIES.find(c => c.id === tpl._categoryId)?.label || tpl._categoryId,
        ],
        q,
      );
    } else if (activeCat === '__saved__') {
      base = savedLayouts.map(sl => ({
        id: `saved-${sl.id}`,
        label: sl.label,
        blocks: [sl.block],
        _saved: true,
        _savedId: sl.id,
        _categoryId: '__saved__',
      }));
    } else {
      base = (SECTION_TEMPLATES[activeCat] || []).map(tpl => ({ ...tpl, _categoryId: activeCat }));
    }

    // Apply filter chips (skip filters entirely on the saved-layouts tab
    // since user-saved sections don't have tag metadata).
    if (activeFilterCount > 0 && activeCat !== '__saved__') {
      base = base.filter(tpl => matchesFilters(tpl, tpl._categoryId, filters));
    }
    return base;
  }, [query, activeCat, savedLayouts, filters, activeFilterCount]);

  if (!open) return null;

  const currentCatLabel = SECTION_CATEGORIES.find(c => c.id === activeCat)?.label || 'Sections';

  return createPortal(
    <>
      {/* Iter 198f — right slide-in panel replaces centered modal so
          the canvas remains visible. Clicking scrim closes. */}
      <div
        className="fixed inset-0 z-[200] bg-black/25 backdrop-blur-[2px]"
        onClick={onClose}
        data-testid="add-section-scrim"
      />
      <div
        className="fixed top-0 right-0 bottom-0 z-[210] flex animate-[slide-in-right_240ms_cubic-bezier(0.22,1,0.36,1)]"
        style={{ width: '100%', maxWidth: 1040 }}
        data-testid="add-section-modal"
      >
      <style>{`@keyframes slide-in-right { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
      <div
        className="relative bg-white shadow-[-20px_0_80px_-20px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden w-full"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* HEADER — title + search + close */}
        <div className="flex items-center gap-4 px-7 py-5 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#6366F1] flex items-center justify-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <div>
              <h2 className="text-[17px] font-bold text-gray-900 leading-none">Section Library</h2>
              <p className="text-[11px] text-gray-500 mt-1">Pick a layout — preview it, insert it instantly.</p>
            </div>
          </div>
          <div className="flex-1 max-w-xl mx-auto relative">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Try "Hero", "Donation", "Pricing", "Restaurant"…'
              className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-3 py-2.5 text-[13px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#8B5CF6] focus:bg-white transition-all"
              data-testid="section-search"
            />
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center transition-all"
            data-testid="add-section-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 flex min-h-0">
          {/* LEFT — sticky category rail */}
          <aside className="w-[230px] shrink-0 border-r border-gray-100 overflow-y-auto py-5 px-3 bg-gray-50/50">
            {/* Iter 149 — "My Layouts" featured tab at top */}
            <div className="mb-5">
              <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Mine</p>
              <button
                onClick={() => { setActiveCat('__saved__'); setQuery(''); }}
                className={`w-full text-left px-3 py-2 mb-0.5 rounded-lg text-[13px] font-medium flex items-center justify-between gap-2 transition-all ${
                  !query && activeCat === '__saved__'
                    ? 'bg-white text-[#5B21B6] shadow-[0_2px_8px_rgba(91,33,182,0.10)] ring-1 ring-[#8B5CF6]/15'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-white/80'
                }`}
                data-testid="section-cat-saved"
              >
                <span className="flex items-center gap-2">
                  <Bookmark size={13} />
                  My Layouts
                </span>
                {savedLayouts.length > 0 && (
                  <span className="text-[10px] bg-gray-200 text-gray-700 px-1.5 rounded-full font-bold">{savedLayouts.length}</span>
                )}
              </button>
            </div>
            {GROUP_ORDER.map(group => {
              const cats = groupedCats[group];
              if (!cats || !cats.length) return null;
              return (
                <div key={group} className="mb-5">
                  <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">{group}</p>
                  {cats.map(cat => {
                    const isActive = !query && activeCat === cat.id;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => { setActiveCat(cat.id); setQuery(''); }}
                        className={`w-full text-left px-3 py-2 mb-0.5 rounded-lg text-[13px] font-medium flex items-center gap-2.5 transition-all ${
                          isActive
                            ? 'bg-white text-[#5B21B6] shadow-[0_2px_8px_rgba(91,33,182,0.10)] ring-1 ring-[#8B5CF6]/15'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-white/80'
                        }`}
                        data-testid={`section-cat-${cat.id}`}
                      >
                        <span className="text-base leading-none">{CATEGORY_ICONS[cat.id] || '◻︎'}</span>
                        <span className="flex-1">{cat.label}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </aside>

          {/* RIGHT — template cards */}
          <div className="flex-1 overflow-y-auto px-8 py-6 bg-white">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h3 className="text-[22px] font-bold text-gray-900 tracking-tight">
                  {query ? <>Search results for <span>&ldquo;{query}&rdquo;</span></>
                   : activeCat === '__saved__' ? 'My Layouts'
                   : currentCatLabel}
                </h3>
                <p className="text-gray-500 text-[13px] mt-1">
                  {visibleTemplates.length} {visibleTemplates.length === 1 ? 'layout' : 'layouts'}
                  {!query && activeCat === '__saved__' && visibleTemplates.length === 0 && ' · Hit the bookmark icon on any section to save it here'}
                  {!query && activeCat !== '__saved__' && ' · Click any card to drop it on your page'}
                  {activeFilterCount > 0 && ` · ${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} active`}
                </p>
              </div>

              {/* Iter 189d Phase C — Filter toggle. Hidden on the saved-layouts tab. */}
              {activeCat !== '__saved__' && (
                <button
                  type="button"
                  onClick={() => setFiltersOpen(o => !o)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-[12px] font-semibold transition-all ${
                    activeFilterCount > 0 || filtersOpen
                      ? 'bg-[#8B5CF6] text-white shadow-[0_4px_14px_rgba(139,92,246,0.35)]'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid="section-filters-toggle"
                  aria-expanded={filtersOpen}
                >
                  <SlidersHorizontal size={13} />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-[#5B21B6] text-[10px] font-bold" data-testid="section-filters-count">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              )}
            </div>

            {/* Iter 189d Phase C — Filter chip row */}
            {activeCat !== '__saved__' && filtersOpen && (
              <div className="mb-6 space-y-3 p-4 rounded-xl border border-gray-100 bg-gray-50" data-testid="section-filters-panel">
                {FILTER_DIMENSIONS.map(dim => (
                  <div key={dim.key} className="flex items-center gap-3 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-gray-500 min-w-[70px]">{dim.label}</span>
                    <div className="flex flex-wrap gap-2">
                      {dim.options.map(opt => {
                        const active = filters[dim.key].has(opt.id);
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => toggleFilter(dim.key, opt.id)}
                            className={`px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-all ${
                              active
                                ? 'bg-[#0E0E23] text-white ring-2 ring-[#8B5CF6]/50'
                                : 'bg-white text-gray-700 border border-gray-200 hover:border-[#8B5CF6] hover:text-[#5B21B6]'
                            }`}
                            data-testid={`section-filter-${dim.key}-${opt.id}`}
                            aria-pressed={active}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {activeFilterCount > 0 && (
                  <div className="pt-2 flex items-center justify-end border-t border-gray-200">
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="text-[11px] font-semibold text-gray-500 hover:text-[#5B21B6] transition-colors"
                      data-testid="section-filters-clear"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}

            {visibleTemplates.length === 0 ? (
              <div className="text-center py-24 text-gray-400">
                <Search size={36} className="mx-auto mb-4 opacity-40" />
                <p className="text-[15px] font-medium text-gray-600">
                  {query
                    ? <>No layouts match &ldquo;{query}&rdquo;</>
                    : activeFilterCount > 0
                      ? 'No layouts match those filters'
                      : 'Nothing here yet'}
                </p>
                <p className="text-[12px] mt-2">
                  {activeFilterCount > 0
                    ? <>Try relaxing a filter — <button type="button" onClick={clearFilters} className="underline font-semibold hover:text-[#5B21B6]" data-testid="section-empty-clear-filters">clear all filters</button></>
                    : 'Try a different word, or pick a category on the left.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {visibleTemplates.map((tpl) => {
                  const badge = TEMPLATE_BADGES[tpl.id];
                  const catLabel = SECTION_CATEGORIES.find(c => c.id === tpl._categoryId)?.label;
                  return (
                    <button
                      key={`${tpl._categoryId}-${tpl.id}`}
                      type="button"
                      onClick={() => { onAdd(tpl); onClose(); }}
                      onMouseEnter={() => scheduleHoverPreview(tpl)}
                      onMouseLeave={cancelHoverPreview}
                      onFocus={() => scheduleHoverPreview(tpl)}
                      onBlur={cancelHoverPreview}
                      className="group relative text-left bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-[#8B5CF6] hover:shadow-[0_18px_50px_-15px_rgba(91,33,182,0.30)] hover:-translate-y-1 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-[#8B5CF6]/20"
                      data-testid={`section-tpl-${tpl.id}`}
                      aria-label={`Insert ${tpl.label}`}
                    >
                      {/* Delete (saved layouts only) */}
                      {tpl._saved && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteSaved(tpl._savedId); }}
                          className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-white/95 text-red-500 hover:bg-red-500 hover:text-white shadow-md flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                          data-testid={`saved-layout-delete-${tpl._savedId}`}
                          title="Delete saved layout"
                        >
                          <Trash2 size={13} />
                        </span>
                      )}
                      {/* Preview — swaps to LIVE render on hover/focus inside the tile itself */}
                      <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                        <div className="absolute inset-0 transform-gpu group-hover:scale-[1.02] transition-transform duration-500">
                          {previewTpl && previewTpl.id === tpl.id && previewTpl._categoryId === tpl._categoryId && hasLiveRenderer(tpl)
                            ? <LiveSectionPreview tpl={tpl} />
                            : <RichTilePreview tpl={tpl} categoryId={tpl._categoryId} />}
                        </div>
                        {/* Hover overlay + Insert pill */}
                        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0E0E23]/60 via-[#0E0E23]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                        <div className="absolute inset-x-0 bottom-3 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 pointer-events-none">
                          <span className="bg-white text-[#0E0E23] px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider shadow-xl flex items-center gap-2">
                            <Sparkles size={12} className="text-[#8B5CF6]" />
                            Insert Section
                          </span>
                        </div>
                        {/* Badge */}
                        {badge && (
                          <span
                            className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider"
                            style={{ background: BADGE_STYLES[badge].bg, color: BADGE_STYLES[badge].fg }}
                            data-testid={`section-tpl-badge-${tpl.id}`}
                          >
                            {BADGE_STYLES[badge].label}
                          </span>
                        )}
                        {/* Iter 189c — live-preview indicator: this template renders with the REAL block code */}
                        {hasLiveRenderer(tpl) && (
                          <span
                            className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/60 text-white text-[9px] font-bold uppercase tracking-wider backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`section-tpl-live-${tpl.id}`}
                          >
                            <Eye size={9} /> Live
                          </span>
                        )}
                      </div>
                      {/* Footer */}
                      <div className="px-4 py-3.5 flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold text-gray-900 truncate">{tpl.label}</p>
                          {query && catLabel && (
                            <p className="text-[10px] text-gray-400 mt-0.5">{catLabel}</p>
                          )}
                        </div>
                        <span className="text-[#8B5CF6] text-sm opacity-0 group-hover:opacity-100 group-hover:translate-x-0 -translate-x-2 transition-all">→</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-7 py-3 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-500">
          <span>Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono">Esc</kbd> to close</span>
          <span>Made with <span className="text-gray-900 font-semibold">Entreprenatives</span></span>
        </div>
      </div>
    </div>
    </>,
    document.body
  );
}
